import { FC, useMemo, useCallback, useState } from 'react'
import { useShallowSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { killFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import Button from 'components/features/pfm/Button/Button'
import { StripeRoot } from 'components/features/pfm/StripeRoot/StripeRoot'
import { subscriptionSelector } from 'selectors/subscriptionSelectors'
import { cleanupSubscriptionMessagesActionCreator, CreateSubscriptionAction, PatchSubscriptionAction } from 'actions/subscriptionActions'
import { SubscriptionPanel, PlanOption } from 'components/features/premium/SubscriptionPanel/SubscriptionPanel'
import { MONTHLY_SUBSCRIPTION_FEE, YEARLY_SUBSCRIPTION_FEE } from 'constants/subscription'
import { CANCEL_BUTTON_PROPS, KillAllPanesButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { SubscriptionPanePreset } from 'components/features/pfm/SubscriptionPane/SubscriptionPanePreset'

type SubscriptionPaymentPaneProps = {
  isEdit: boolean
}

export const SubscriptionPaymentPane: FC<SubscriptionPaymentPaneProps> = (props) => {
  const { isEdit } = props

  const [isStripeReady, setIsStripeReady] = useState(false)
  const handleStripeReady = useCallback(() => {
    setIsStripeReady(true)
  }, [])

  const dispatch = useStoreDispatch()
  const [plan, setPlan] = useState<PlanOption>('monthly')
  const handlePlanChange = useCallback((nextPlan) => {
    dispatch(cleanupSubscriptionMessagesActionCreator())
    setPlan(nextPlan)
  }, [dispatch])

  const isLoading = useShallowSelector((state) => {
    const { stripeCreatePaymentMethod, stripePatchPaymentMethod } = subscriptionSelector(state)

    return stripeCreatePaymentMethod.isLoading || stripePatchPaymentMethod.isLoading
  })

  const handleClose = useCallback(() => {
    dispatch(killFloatingPanesActionCreator('subscription'))
  }, [dispatch])

  const handlePay = useCallback(async () => {
    const amount = plan === 'annual' ? YEARLY_SUBSCRIPTION_FEE : MONTHLY_SUBSCRIPTION_FEE

    if (isEdit) {
      dispatch(new PatchSubscriptionAction({ amount }))
      return
    }

    dispatch(cleanupSubscriptionMessagesActionCreator())
    dispatch(new CreateSubscriptionAction({ amount }))

  }, [dispatch, isEdit, plan])

  const finishButtonChild = useMemo(() => (
    <Button
      size='medium'
      theme='green'
      text='Finish'
      qaMarker='save-button'
      onClick={ handlePay }
      disabled={ isLoading || !isStripeReady }
      isSubmitting={ isLoading }
    />
  ), [handlePay, isLoading, isStripeReady])

  const cancelButtonChild = useMemo(() => (
    <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } disabled={ isLoading } />
  ), [isLoading])

  return (
    <StripeRoot>
      <SubscriptionPanePreset
        title='Upgrade Your Membership'
        primaryButton={ finishButtonChild }
        cancelButton={ cancelButtonChild }
        isLoading={ isLoading }
      >
        <SubscriptionPanel
          value={ plan }
          onChange={ handlePlanChange }
          onClose={ handleClose }
          onStripeReady={ handleStripeReady }
        />
      </SubscriptionPanePreset>
    </StripeRoot>
  )
}
